import { debounce, throttle } from 'lodash';
import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ascending } from 'd3-array';
import { stratify, pack } from 'd3-hierarchy';
import { getList, getRecord } from '../../../services/deepstream';
import { insertNode, moveNode, setDraggedNodeId } from '../../../services/board/board';
import { useCurrentBoardState } from '../../../services/state';
import { getElementSize } from '../../../text-utils';
import GraphNode from './GraphNode';

const zoomPaddingInPx = 25;

const Graph = () => {
  const { boardId } = useParams();
  const [nodeList, setNodeList] = useState([]);
  const [{ width, height }, setSize] = useState({ width: 0, height: 0 });

  const boardState = useCurrentBoardState();
  const { zoomNodeId, moveWithChildren } = boardState.get();
  const zoomNode = zoomNodeId ? nodeList.find(node => node.id === zoomNodeId) : null;

  const zoomReferenceSize = Math.min(width, height) - 2 * zoomPaddingInPx;
  const zoomScale = zoomNode && zoomReferenceSize > 0 ? zoomReferenceSize / zoomNode.r / 2 : 1;
  const zoomTranslateX = zoomNode && width ? (width / 2 - zoomNode.x) * zoomScale : 0;
  const zoomTranslateY = zoomNode && height ? (height / 2 - zoomNode.y) * zoomScale : 0;

  const setCanvasSize = useCallback(
    debounce(() => setSize(getElementSize('tree')), 100),
    []
  );
  const handleResize = useCallback(() => {
    boardState.zoomNodeId.set(null);
    setCanvasSize();
  }, [boardState.zoomNodeId, setCanvasSize]);

  useLayoutEffect(setCanvasSize, [setCanvasSize]);
  useEffect(() => {
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  const drawBoard = useCallback(async () => {
    if (!width || !height) return;

    const nodeListRecord = getList(`definition/${boardId}`);
    await nodeListRecord.whenReady();
    const circleList = await Promise.all(
      nodeListRecord.getEntries().map(async circleRecordId => {
        const circleRecord = getRecord(circleRecordId);
        await circleRecord.whenReady();
        const circleDetails = circleRecord.get();
        return { ...circleDetails };
      })
    );

    if (!circleList.length) return;

    try {
      const stratifiedNodeList = stratify()
        .id(node => node.id)
        .parentId(node => node.parentId)(circleList);

      const sortedNodeList = stratifiedNodeList
        .count()
        .sort((x, y) => ascending(x.data.timestamp, y.data.timestamp));

      const packed = pack().size([width, height]).padding(10)(sortedNodeList);
      const [rootNode, ...nodesToDisplay] = packed.descendants();

      setNodeList(nodesToDisplay);
    } catch (error) {
      console.error('error while calculating board graph', error);
    }
  }, [boardId, width, height]);

  useEffect(() => {
    const nodeListRecord = getList(`definition/${boardId}`);
    nodeListRecord.subscribe(drawBoard, true);

    return () => nodeListRecord.unsubscribe(drawBoard);
  }, [boardId, drawBoard]);

  const handleDragEnter = useCallback(
    event => {
      event.preventDefault();
      boardState.activeDropTargetId.set('root');
    },
    [boardState.activeDropTargetId]
  );

  const handleDrop = useCallback(
    event => {
      const circleId = event.dataTransfer.getData('circleId');
      const internalDrag = event.dataTransfer.getData('internal');
      setDraggedNodeId(null);
      boardState.activeDropTargetId.set(null);

      if (internalDrag) {
        moveNode({ nodeId: circleId, targetNodeId: 'root', withChildren: moveWithChildren });
      } else {
        insertNode({ circleId: circleId.split('/')[2], parentId: 'root' });
      }
    },
    [boardState.activeDropTargetId, moveWithChildren]
  );

  const handleWheelEvent = useCallback(
    throttle(event => {
      if (event.nativeEvent.wheelDelta < 0) {
        boardState.zoomNodeId.set(null);
      }
    }, 100),
    [boardState.zoomNodeId]
  );

  return (
    <div className="content" style={{ overflow: 'hidden' }}>
      <div
        id="tree"
        className="tree"
        style={{
          position: 'relative',
          transform: `translate(${zoomTranslateX}px, ${zoomTranslateY}px) scale(${zoomScale})`,
          transition: '400ms all'
        }}
        onDragEnter={handleDragEnter}
        onDragOver={event => event.preventDefault()}
        onDrop={handleDrop}
        onWheel={handleWheelEvent}
      >
        {nodeList.map(node => (
          <GraphNode key={node.id} node={node} drawBoard={drawBoard} />
        ))}
      </div>
    </div>
  );
};

export default Graph;
